import { Button, Modal, Typography, List } from 'antd';
import { Box, Grid, Typography as MuiTypography } from '@mui/material';
import { EyeOutlined } from '@ant-design/icons';

export interface AssetPreviewDocument {
    label: string;
    base64: string | null;
}

interface AssetDocumentPreviewModalProps {
    open: boolean;
    docs: AssetPreviewDocument[];
    activeIndex: number | null;
    onSelect: (index: number) => void;
    onBack: () => void;
    onClose: () => void;
}

const getMimeType = (base64: string | null) => {
    if (!base64) return '';
    return base64.match(/data:([^;]+);base64/)?.[1] || '';
};

const AssetDocumentPreviewModal = ({
    open,
    docs,
    activeIndex,
    onSelect,
    onBack,
    onClose
}: AssetDocumentPreviewModalProps) => {
    const activeDoc = activeIndex !== null ? docs[activeIndex] : null;
    const activeMime = activeDoc ? getMimeType(activeDoc.base64) : '';

    return (
        <Modal
            open={open}
            title={activeDoc ? activeDoc.label : 'Documents de l\'actif'}
            onCancel={onClose}
            footer={activeDoc && docs.length > 1 ? [
                <Button key="back" onClick={onBack}>Retour à la galerie</Button>,
                <Button key="close" onClick={onClose}>Fermer</Button>
            ] : [<Button key="close" onClick={onClose}>Fermer</Button>]}
            width="95vw"
            style={{ maxWidth: '1500px' }}
            zIndex={2000}
            centered
            bodyStyle={{ padding: 16, background: '#f5f5f5' }}
        >
            {!activeDoc ? (
                docs.length > 0 ? (
                    <List
                        dataSource={docs}
                        renderItem={(doc, index) => {
                            const mime = getMimeType(doc.base64);
                            return (
                                <List.Item
                                    key={`${doc.label}-${index}`}
                                    onClick={() => onSelect(index)}
                                    style={{ cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: 4, marginBottom: 8, padding: 12, background: '#fff' }}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            doc.base64 ? (
                                                mime.startsWith('image/') ? (
                                                    <img src={doc.base64} alt={doc.label} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                                                ) : (
                                                    <Box sx={{ width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f0f0', borderRadius: 4 }}>
                                                        <Typography style={{ fontSize: '10px' }}>PDF</Typography>
                                                    </Box>
                                                )
                                            ) : (
                                                <Box sx={{ width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#e0e0e0', borderRadius: 4 }}>
                                                    <Typography style={{ fontSize: '10px', color: '#666' }}>DOC</Typography>
                                                </Box>
                                            )
                                        }
                                        title={<MuiTypography variant="body2" sx={{ fontWeight: 'bold' }}>{doc.label}</MuiTypography>}
                                        description={<MuiTypography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>Cliquez pour prévisualiser</MuiTypography>}
                                    />
                                    <EyeOutlined style={{ fontSize: 20, color: '#2A6596' }} />
                                </List.Item>
                            );
                        }}
                    />
                ) : (
                    <Typography>Aucun document disponible.</Typography>
                )
            ) : (
                <Box sx={{ minHeight: '70vh', maxHeight: '80vh', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e8e8e8' }}>
                        <MuiTypography variant="subtitle2" fontWeight="bold" color="#2A6596">
                            {activeDoc.label}
                        </MuiTypography>
                    </Box>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {activeDoc.base64 ? (
                            activeMime.startsWith('image/') ? (
                                <img src={activeDoc.base64} alt={activeDoc.label} style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', borderRadius: 6 }} />
                            ) : activeMime === 'application/pdf' ? (
                                <iframe src={activeDoc.base64} title={activeDoc.label} style={{ width: '100%', height: '65vh', border: 0, background: '#fff' }} />
                            ) : (
                                <Typography>Aperçu non disponible pour ce type de fichier.</Typography>
                            )
                        ) : (
                            <MuiTypography color="textSecondary">Document disponible mais contenu non chargé.</MuiTypography>
                        )}
                    </div>
                </Box>
            )}
        </Modal>
    );
};

export default AssetDocumentPreviewModal;
